/**
 * layout — the team page's binding of the shared detail-page surface.
 *
 * A team has four panes over its canvas:
 *
 *   context   status, counts, when it was created, its practice areas   (left)
 *   access    the members list, and the one way into changing it        (right)
 *   activity  the audit log                                            (right)
 *   reader    a document, read without leaving the page      (right, no rail)
 *
 * The centre is the CANVAS — the tab bar and whichever tab is open (Details,
 * Feed, Documents).
 *
 * WHAT THIS REPLACES. The Details tab was a `lg:grid-cols-3` with a hardcoded
 * right column holding a hand-rolled Members card (its own avatar list, its
 * own "+N more" tail, and a Manage button carrying a 30-line side-panel
 * payload inline in JSX). That column only existed on Details, so "who is on
 * this team" left the screen the moment anyone opened the Feed. The audit log
 * was worse: a `fixed inset-y-0 right-0` ActivityDrawer with its own scrim,
 * living outside every layout the page had, plus a `RecentActivityCard` on
 * the canvas showing a truncated copy of the same log.
 *
 * All three are panes now, so they dock, stack, reorder, resize and persist
 * per user per team, and they stay to hand whatever the canvas is showing.
 *
 * WHY ENTITY SCOPE. A team read from its documents and one read from its
 * members want different panes open; one shared key would make every team
 * inherit the arrangement of whichever was opened last.
 *
 * NO LEGACY KEYS — this surface is new, so there is nothing to migrate.
 *
 * THE READER IS A PANE, NOT A MODAL. The Documents tab used to open a file in
 * an `attachmentViewer` modal over the page it belongs to, so the list it was
 * opened from was covered by it. Contacts already replaced that same modal
 * with a pane; this is the same move for the org units. `DocumentsTab` reaches
 * it through `Shared/OrgSurfaceContext`.
 *
 * NO DETAIL PANE. Nothing on a team page selects a row that a shared detail
 * container would edit, so a pane declared for one could only ever open onto
 * nothing.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../../shared/surface/defineDetailSurface';

export type TeamPanelId = 'context' | 'access' | 'activity' | 'reader';

/** The canvas's tabs. Labels are the page's — see `TeamDetail`. */
export type TeamTab = 'Details' | 'Feed' | 'Documents';

export const TEAM_TABS: TeamTab[] = ['Details', 'Feed', 'Documents'];

export type TeamPanelSlot = GenericSlot<TeamPanelId>;
export type TeamPanelLayout = GenericLayout<TeamPanelId>;

export const TEAM_PANEL_IDS: TeamPanelId[] = ['context', 'access', 'activity', 'reader'];

/**
 * ONE PANE IS OPEN BY DEFAULT — context — because the facts it holds (status,
 * member count, created, practice areas) are exactly what the fixed layout
 * showed without being asked. Access and the log open deliberately, from the
 * rail or from the overflow menu, which is what the drawer and the Manage
 * button did before.
 */
const TEAM_PANES: Record<TeamPanelId, DetailPaneConfig> = {
  context: {
    label: 'Context',
    bounds: { min: 260, max: 720 },
    dock: 'left', index: 0, width: 320, visible: true, rail: true,
  },
  access: {
    label: 'Members',
    bounds: { min: 260, max: 640 },
    dock: 'right', index: 0, width: 320, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 1, width: 340, visible: false, rail: true,
  },
  /**
   * NO RAIL PILL. The reader opens because someone pressed a document in the
   * Documents tab; a toggle for it would be a control that does nothing until
   * you use the page, and would open onto "nothing selected".
   */
  reader: {
    label: 'Reader',
    bounds: { min: 320, max: 900 },
    dock: 'right', index: 2, width: 560, visible: false,
  },
};

export const TEAM_SURFACE = defineDetailSurface<TeamPanelId, TeamTab>({
  kind: 'detail',
  // Also the `data-testid` prefix for the shell: `team-surface`, etc.
  name: 'team',
  scope: 'entity',
  ids: TEAM_PANEL_IDS,
  panes: TEAM_PANES,
  tabs: TEAM_TABS,
  readerPane: 'reader',
});

/* ── derived, for call sites and tests that want the plain records ── */

export const TEAM_PANEL_LABELS: Record<TeamPanelId, string> = TEAM_SURFACE.labels;
export const TEAM_PANEL_BOUNDS: Record<TeamPanelId, PanelBounds> = TEAM_SURFACE.bounds;
export const TEAM_DEFAULT_LAYOUT: TeamPanelLayout = TEAM_SURFACE.defaults;
